import { TermForm, TermFormLocation, VendorContractRow, SellingOwnerEmploymentAgreementRow } from './term-form';

export type PendingFieldChangeKey =
    | 'leaseAssumption'
    | 'vendorAssumption'
    | 'ownerEmployedPostClose'
    | 'ownerEmploymentDuration';

export interface PendingFieldChange {
    id: string;
    fieldKey: PendingFieldChangeKey;
    rowKey: string | null;
    proposedValue: string;
    dateCreated: string;
}

export function findPendingFieldChange(
    changes: PendingFieldChange[] | undefined,
    fieldKey: PendingFieldChangeKey,
    rowKey: string | null
): PendingFieldChange | undefined {
    return changes?.find((change) => change.fieldKey === fieldKey && change.rowKey === rowKey);
}

export function groupPendingFieldChangesByRowKey(
    changes: PendingFieldChange[] | undefined,
    fieldKey: PendingFieldChangeKey
): Record<string, PendingFieldChange> {
    const changesByRowKey: Record<string, PendingFieldChange> = {};
    for (const change of changes ?? []) {
        if (change.fieldKey === fieldKey && change.rowKey != null) {
            changesByRowKey[change.rowKey] = change;
        }
    }
    return changesByRowKey;
}

export function parseOwnerEmployedPostCloseValue(proposedValue: string): boolean | null {
    if (proposedValue === 'true') return true;
    if (proposedValue === 'false') return false;
    return null;
}

export function parseOwnerEmploymentDurationValue(proposedValue: string): number | null {
    if (proposedValue === '') return null;
    const parsed = Number(proposedValue);
    return Number.isFinite(parsed) ? parsed : null;
}

function serializeLeaseAssumptionValue(value: TermFormLocation['leaseAssumption'] | undefined): string {
    return value === 'new' || value === 'purchase' ? value : 'existing';
}

function serializeVendorAssumptionValue(value: boolean | null | undefined): string {
    return value === true ? 'true' : 'false';
}

function serializeOwnerEmployedPostCloseValue(value: boolean | null | undefined): string {
    if (value === true) return 'true';
    if (value === false) return 'false';
    return '';
}

function serializeOwnerEmploymentDurationValue(value: number | null | undefined): string {
    return value == null ? '' : String(value);
}

export interface ScopedBaseline {
    leaseAssumptionByLocationId: Record<string, TermFormLocation['leaseAssumption'] | undefined>;
    vendorAssumptionByTableRowId: Record<string, boolean | null | undefined>;
    ownerEmployedPostCloseByTableRowId: Record<string, boolean | null | undefined>;
    ownerEmploymentDurationByTableRowId: Record<string, number | null | undefined>;
}

export function buildScopedBaseline(termForm: TermForm | null | undefined): ScopedBaseline {
    const leaseAssumptionByLocationId: Record<string, TermFormLocation['leaseAssumption'] | undefined> = {};
    for (const location of termForm?.locations ?? []) {
        leaseAssumptionByLocationId[location.locationId] = location.leaseAssumption;
    }
    const vendorAssumptionByTableRowId: Record<string, boolean | null | undefined> = {};
    for (const row of termForm?.vendorContractRows ?? []) {
        vendorAssumptionByTableRowId[String(row.tableRowId)] = row.isAssumedByBuyer;
    }
    const ownerEmployedPostCloseByTableRowId: Record<string, boolean | null | undefined> = {};
    const ownerEmploymentDurationByTableRowId: Record<string, number | null | undefined> = {};
    for (const row of termForm?.sellingOwnerEmploymentAgreementRows ?? []) {
        const rowKey = String(row.tableRowId);
        ownerEmployedPostCloseByTableRowId[rowKey] = row.isEmployedPostClose ?? null;
        ownerEmploymentDurationByTableRowId[rowKey] = row.durationMonths ?? null;
    }
    return {
        leaseAssumptionByLocationId,
        vendorAssumptionByTableRowId,
        ownerEmployedPostCloseByTableRowId,
        ownerEmploymentDurationByTableRowId,
    };
}

export function deriveAdminScopedPendingChanges(
    termForm: TermForm,
    baseline: ScopedBaseline
): PendingFieldChange[] {
    const pendingFieldChanges: PendingFieldChange[] = [];

    const addIfChanged = (fieldKey: PendingFieldChangeKey, rowKey: string, baselineValue: string, proposedValue: string) => {
        if (baselineValue === proposedValue) {
            return;
        }
        pendingFieldChanges.push({
            id: `optimistic-${fieldKey}-${rowKey}`,
            fieldKey,
            rowKey,
            proposedValue,
            dateCreated: new Date().toISOString(),
        });
    };

    const locations = termForm.locations ?? [];
    const locationIds = new Set([...Object.keys(baseline.leaseAssumptionByLocationId), ...locations.map((location) => location.locationId)]);
    for (const locationId of Array.from(locationIds)) {
        const location = locations.find((l) => l.locationId === locationId);
        addIfChanged(
            'leaseAssumption',
            locationId,
            serializeLeaseAssumptionValue(baseline.leaseAssumptionByLocationId[locationId]),
            serializeLeaseAssumptionValue(location?.leaseAssumption)
        );
    }

    const vendorContractRows = termForm.vendorContractRows ?? [];
    const vendorTableRowIds = new Set([
        ...Object.keys(baseline.vendorAssumptionByTableRowId),
        ...vendorContractRows.map((row) => String(row.tableRowId)),
    ]);
    for (const rowKey of Array.from(vendorTableRowIds)) {
        const row = vendorContractRows.find((r) => String(r.tableRowId) === rowKey);
        addIfChanged(
            'vendorAssumption',
            rowKey,
            serializeVendorAssumptionValue(baseline.vendorAssumptionByTableRowId[rowKey]),
            serializeVendorAssumptionValue(row?.isAssumedByBuyer)
        );
    }

    const ownerRows = termForm.sellingOwnerEmploymentAgreementRows ?? [];
    const ownerTableRowIds = new Set([
        ...Object.keys(baseline.ownerEmployedPostCloseByTableRowId),
        ...Object.keys(baseline.ownerEmploymentDurationByTableRowId),
        ...ownerRows.map((row) => String(row.tableRowId)),
    ]);
    for (const rowKey of Array.from(ownerTableRowIds)) {
        const row = ownerRows.find((r) => String(r.tableRowId) === rowKey);
        addIfChanged(
            'ownerEmployedPostClose',
            rowKey,
            serializeOwnerEmployedPostCloseValue(baseline.ownerEmployedPostCloseByTableRowId[rowKey]),
            serializeOwnerEmployedPostCloseValue(row?.isEmployedPostClose)
        );
        addIfChanged(
            'ownerEmploymentDuration',
            rowKey,
            serializeOwnerEmploymentDurationValue(baseline.ownerEmploymentDurationByTableRowId[rowKey]),
            serializeOwnerEmploymentDurationValue(row?.durationMonths)
        );
    }

    return pendingFieldChanges;
}

export function applyBaselineValueToTermForm(
    termForm: TermForm,
    fieldKey: PendingFieldChangeKey,
    rowKey: string,
    baseline: ScopedBaseline
): TermForm {
    switch (fieldKey) {
        case 'leaseAssumption': {
            const baselineValue = baseline.leaseAssumptionByLocationId[rowKey];
            if (baselineValue === undefined) {
                return { ...termForm, locations: (termForm.locations ?? []).filter((location) => location.locationId !== rowKey) };
            }
            return applyPendingChangeToTermForm(termForm, buildBaselineChange(fieldKey, rowKey, serializeLeaseAssumptionValue(baselineValue)));
        }
        case 'vendorAssumption': {
            const baselineValue = baseline.vendorAssumptionByTableRowId[rowKey];
            if (baselineValue === undefined) {
                const tableRowId = Number(rowKey);
                return { ...termForm, vendorContractRows: (termForm.vendorContractRows ?? []).filter((row) => row.tableRowId !== tableRowId) };
            }
            return applyPendingChangeToTermForm(termForm, buildBaselineChange(fieldKey, rowKey, serializeVendorAssumptionValue(baselineValue)));
        }
        case 'ownerEmployedPostClose': {
            const baselineValue = baseline.ownerEmployedPostCloseByTableRowId[rowKey];
            return applyPendingChangeToTermForm(termForm, buildBaselineChange(fieldKey, rowKey, serializeOwnerEmployedPostCloseValue(baselineValue)));
        }
        case 'ownerEmploymentDuration': {
            const baselineValue = baseline.ownerEmploymentDurationByTableRowId[rowKey];
            return applyPendingChangeToTermForm(termForm, buildBaselineChange(fieldKey, rowKey, serializeOwnerEmploymentDurationValue(baselineValue)));
        }
        default:
            return termForm;
    }
}

function buildBaselineChange(fieldKey: PendingFieldChangeKey, rowKey: string, proposedValue: string): PendingFieldChange {
    return {
        id: '',
        fieldKey,
        rowKey,
        proposedValue,
        dateCreated: '',
    };
}

function generateRowId(): string {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function pruneEmptyOwnerEmploymentRow(
    rows: SellingOwnerEmploymentAgreementRow[],
    tableRowId: number
): SellingOwnerEmploymentAgreementRow[] {
    return rows.filter(
        (row) => row.tableRowId !== tableRowId || row.isEmployedPostClose !== null || row.durationMonths !== null
    );
}

export function applyPendingChangeToTermForm(termForm: TermForm, change: PendingFieldChange): TermForm {
    if (change.rowKey == null) {
        return termForm;
    }
    switch (change.fieldKey) {
        case 'leaseAssumption': {
            const leaseAssumption: TermFormLocation['leaseAssumption'] =
                change.proposedValue === 'new' || change.proposedValue === 'purchase' ? change.proposedValue : 'existing';
            const existing = termForm.locations ?? [];
            const locations: TermFormLocation[] = existing.some((location) => location.locationId === change.rowKey)
                ? existing.map((location) =>
                    location.locationId === change.rowKey ? { ...location, leaseAssumption } : location
                  )
                : [...existing, { locationId: change.rowKey, locationName: '', leaseAssumption }];
            return { ...termForm, locations };
        }
        case 'vendorAssumption': {
            const isAssumedByBuyer = change.proposedValue === 'true';
            const tableRowId = Number(change.rowKey);
            const existing = termForm.vendorContractRows ?? [];
            const vendorContractRows: VendorContractRow[] = existing.some((row) => row.tableRowId === tableRowId)
                ? existing.map((row) => (row.tableRowId === tableRowId ? { ...row, isAssumedByBuyer } : row))
                : [...existing, { id: generateRowId(), tableRowId, isAssumedByBuyer }];
            return { ...termForm, vendorContractRows };
        }
        case 'ownerEmployedPostClose': {
            const isEmployedPostClose = parseOwnerEmployedPostCloseValue(change.proposedValue);
            const tableRowId = Number(change.rowKey);
            const existing = termForm.sellingOwnerEmploymentAgreementRows ?? [];
            const updatedRows: SellingOwnerEmploymentAgreementRow[] = existing.some((row) => row.tableRowId === tableRowId)
                ? existing.map((row) => (row.tableRowId === tableRowId ? { ...row, isEmployedPostClose } : row))
                : [...existing, { id: generateRowId(), tableRowId, isEmployedPostClose, durationMonths: null }];
            return { ...termForm, sellingOwnerEmploymentAgreementRows: pruneEmptyOwnerEmploymentRow(updatedRows, tableRowId) };
        }
        case 'ownerEmploymentDuration': {
            const durationMonths = parseOwnerEmploymentDurationValue(change.proposedValue);
            const tableRowId = Number(change.rowKey);
            const existing = termForm.sellingOwnerEmploymentAgreementRows ?? [];
            const updatedRows: SellingOwnerEmploymentAgreementRow[] = existing.some((row) => row.tableRowId === tableRowId)
                ? existing.map((row) => (row.tableRowId === tableRowId ? { ...row, durationMonths } : row))
                : [...existing, { id: generateRowId(), tableRowId, isEmployedPostClose: null, durationMonths }];
            return { ...termForm, sellingOwnerEmploymentAgreementRows: pruneEmptyOwnerEmploymentRow(updatedRows, tableRowId) };
        }
        default:
            return termForm;
    }
}
